import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import type { SelectedFiltersInput } from "@/lib/utils/filters";
import { fetchArticlesPaged } from "@/store/news-feed/feed-content-management/feed-content-management.async-actions";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";

const filtersContentKey = (filters: SelectedFiltersInput | undefined) =>
  JSON.stringify(filters ?? null);

/**
 * Article list pagination: initial fetch, infinite scroll, and intersection observer.
 * @param externalScrollContainerRef - Scroll root for the load-more sentinel (optional).
 */
export const useArticlesPagination = (
  externalScrollContainerRef?: RefObject<HTMLDivElement | null>,
) => {
  const { feedId, startDate, endDate, selectedFilters } =
    useAnalysisParamsForQueries();
  const ordering = useFeedContentManagementStore((state) => state.ordering);

  const pagination = useFeedContentManagementStore((state) => state.pagination);
  const isFetchingArticles = useFeedContentManagementStore(
    (state) => state.isFetchingArticles,
  );

  const hasTriggeredLoad = useRef(false);
  const selectedFiltersRef = useRef<SelectedFiltersInput | undefined>(
    selectedFilters,
  );
  selectedFiltersRef.current = selectedFilters;

  const selectedFiltersDigest = filtersContentKey(selectedFilters);

  const fetchArticles = useCallback(async () => {
    if (!feedId || !startDate || !endDate) return;

    hasTriggeredLoad.current = false;
    useFeedContentManagementStore.getState().resetState();
    await fetchArticlesPaged(
      feedId,
      startDate,
      endDate,
      selectedFiltersRef.current,
    );
  }, [feedId, startDate, endDate, selectedFiltersDigest]);

  const fetchMoreArticles = useCallback(async () => {
    if (
      !feedId ||
      !startDate ||
      !endDate ||
      !pagination.hasNextPage ||
      isFetchingArticles
    )
      return;

    hasTriggeredLoad.current = true;
    await fetchArticlesPaged(
      feedId,
      startDate,
      endDate,
      selectedFiltersRef.current,
      pagination.endCursor
        ? {
            after: pagination.endCursor,
          }
        : undefined,
      true,
    ).finally(() => {
      hasTriggeredLoad.current = false;
    });
  }, [
    feedId,
    startDate,
    endDate,
    selectedFiltersDigest,
    pagination.hasNextPage,
    pagination.endCursor,
    isFetchingArticles,
  ]);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    root: externalScrollContainerRef?.current,
  });

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles, ordering]);

  useEffect(() => {
    if (
      inView &&
      pagination.hasNextPage &&
      !isFetchingArticles &&
      feedId &&
      startDate &&
      endDate &&
      !hasTriggeredLoad.current
    ) {
      fetchMoreArticles();
    }
  }, [
    inView,
    pagination.hasNextPage,
    pagination.endCursor,
    isFetchingArticles,
    fetchMoreArticles,
    feedId,
    startDate,
    endDate,
  ]);

  return {
    loadMoreRef,
    pagination,
    isFetchingArticles,
  };
};
